export type MetadataColumnType =
  | "string"
  | "int"
  | "float"
  | "bool"
  | "list"
  | "dict"

export const METADATA_COLUMN_TYPE_LABELS: Record<MetadataColumnType, string> = {
  string: "string",
  int: "int",
  float: "float",
  bool: "bool",
  list: "list",
  dict: "dict",
}

export function inferValueType(value: unknown): MetadataColumnType | null {
  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === "boolean") {
    return "bool"
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number.isInteger(value) ? "int" : "float"
  }
  if (typeof value === "string") {
    return "string"
  }
  if (Array.isArray(value)) {
    return "list"
  }
  if (typeof value === "object") {
    return "dict"
  }
  return null
}

/**
 * Combine two observed types for the same key. `int` + `float` widens to
 * `float`; any other mismatch falls back to `dict` so the raw-JSON editor
 * is used.
 */
export function unifyTypes(
  a: MetadataColumnType | null,
  b: MetadataColumnType | null,
): MetadataColumnType | null {
  if (a === null) {
    return b
  }
  if (b === null) {
    return a
  }
  if (a === b) {
    return a
  }
  if ((a === "int" && b === "float") || (a === "float" && b === "int")) {
    return "float"
  }
  return "dict"
}

export function formatValueForDisplay(
  value: unknown,
  type: MetadataColumnType,
): string {
  if (value === null || value === undefined) {
    return ""
  }
  if (type === "string" && typeof value === "string") {
    return value
  }
  if ((type === "int" || type === "float") && typeof value === "number") {
    return String(value)
  }
  if (type === "bool" && typeof value === "boolean") {
    return value ? "true" : "false"
  }
  return JSON.stringify(value)
}

/**
 * Parse a user-edited string back into the typed value. Returns
 * `{ ok: true, value }` on success, `{ ok: false, error }` otherwise.
 * Empty strings are treated as "clear the value" and return `null`.
 */
export function parseValueFromString(
  raw: string,
  type: MetadataColumnType,
): { ok: true; value: unknown } | { ok: false; error: string } {
  const trimmed = raw.trim()
  if (trimmed === "") {
    return { ok: true, value: null }
  }

  switch (type) {
    case "string":
      return { ok: true, value: raw }
    case "int": {
      const n = Number(trimmed)
      if (!Number.isFinite(n) || !Number.isInteger(n)) {
        return { ok: false, error: "Must be an integer" }
      }
      return { ok: true, value: n }
    }
    case "float": {
      const n = Number(trimmed)
      if (!Number.isFinite(n)) {
        return { ok: false, error: "Must be a number" }
      }
      return { ok: true, value: n }
    }
    case "bool": {
      const lower = trimmed.toLowerCase()
      if (lower === "true") {
        return { ok: true, value: true }
      }
      if (lower === "false") {
        return { ok: true, value: false }
      }
      return { ok: false, error: "Must be true or false" }
    }
    case "list":
    case "dict": {
      let parsed: unknown
      try {
        parsed = JSON.parse(trimmed) as unknown
      } catch {
        return { ok: false, error: "Invalid JSON" }
      }
      if (type === "list" && !Array.isArray(parsed)) {
        return { ok: false, error: "Must be a JSON array" }
      }
      if (
        type === "dict" &&
        (parsed === null || Array.isArray(parsed) || typeof parsed !== "object")
      ) {
        return { ok: false, error: "Must be a JSON object" }
      }
      return { ok: true, value: parsed }
    }
  }
}
